import { useState, useRef, useEffect, useCallback } from "react";

const historicalData = {
  months: ["10月", "11月", "12月", "1月", "2月", "3月"],
  usage: [22.1, 28.5, 38.9, 34.5, 29.7, 25.9],
  cost: [76.25, 98.33, 134.21, 119.03, 102.47, 89.36],
  avgTemp: [15, 8, 2, -1, 3, 10],
};

const currentMonth = {
  usage: 25.9,
  cost: 89.36,
  avgTemp: 10,
  daysElapsed: 10,
  projectedUsage: 31.2,
  projectedCost: 107.6,
};

const radarDimensions = [
  { label: "用气量", current: 78, history: 85 },
  { label: "费用控制", current: 82, history: 75 },
  { label: "用气效率", current: 88, history: 80 },
  { label: "安全评分", current: 92, history: 90 },
  { label: "缴费及时", current: 95, history: 88 },
  { label: "节能表现", current: 85, history: 72 },
];

const reportTemplates = [
  "月度用气综合分析",
  "与历史同期对比",
  "节能效果评估",
  "费用趋势预测",
];

export default function AICompareReport() {
  const [subTab, setSubTab] = useState<"curve" | "radar" | "report">("curve");
  const [reportText, setReportText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const curveRef = useRef<HTMLCanvasElement>(null);
  const radarRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const radarAnimRef = useRef<number>(0);
  const radarPhaseRef = useRef(0);

  // Draw comparison curve
  useEffect(() => {
    if (subTab !== "curve") return;
    const canvas = curveRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const data = historicalData.usage;
    const allData = [...data, currentMonth.projectedUsage];
    const maxV = Math.max(...allData) + 5;
    const minV = Math.min(...allData) - 3;
    const range = maxV - minV;
    const padL = 28, padR = 12, padT = 16, padB = 24;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const labels = [...historicalData.months, "4月(预)"];

    // Grid
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.strokeStyle = "rgba(0,0,0,0.06)";
      ctx.lineWidth = 1;
      ctx.stroke();
      const val = maxV - (range / 4) * i;
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(val.toFixed(0), padL - 3, y + 3);
    }

    // Historical area fill
    const histGrad = ctx.createLinearGradient(0, padT, 0, H - padB);
    histGrad.addColorStop(0, "rgba(232,83,26,0.15)");
    histGrad.addColorStop(1, "rgba(232,83,26,0)");
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (labels.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.lineTo(padL + ((data.length - 1) / (labels.length - 1)) * chartW, H - padB);
    ctx.lineTo(padL, H - padB);
    ctx.closePath();
    ctx.fillStyle = histGrad;
    ctx.fill();

    // Historical line
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (labels.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#E8531A";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.stroke();

    // Predicted point (dashed line from last historical)
    const lastX = padL + ((data.length - 1) / (labels.length - 1)) * chartW;
    const lastY = padT + chartH - ((data[data.length - 1] - minV) / range) * chartH;
    const predX = padL + ((labels.length - 1) / (labels.length - 1)) * chartW;
    const predY = padT + chartH - ((currentMonth.projectedUsage - minV) / range) * chartH;
    ctx.beginPath();
    ctx.setLineDash([4, 3]);
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(predX, predY);
    ctx.strokeStyle = "rgba(232,83,26,0.5)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);

    // Dots
    data.forEach((v, i) => {
      const x = padL + (i / (labels.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(232,83,26,0.3)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    // Predicted dot
    ctx.beginPath();
    ctx.arc(predX, predY, 3, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(232,83,26,0.5)";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(predX, predY, 6, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(232,83,26,0.25)";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([2, 2]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Labels
    labels.forEach((l, i) => {
      const x = padL + (i / (labels.length - 1)) * chartW;
      ctx.fillStyle = i === labels.length - 1 ? "rgba(232,83,26,0.6)" : "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(l, x, H - 5);
    });

    // Current month marker
    const curX = padL + ((data.length - 1) / (labels.length - 1)) * chartW;
    ctx.fillStyle = "#E8531A";
    ctx.font = "bold 8px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("当前", curX, padT - 4);
  }, [subTab]);

  // Animated radar chart
  const drawRadar = useCallback(() => {
    const canvas = radarRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    radarPhaseRef.current += 0.02;
    const phase = radarPhaseRef.current;
    const cx = W / 2;
    const cy = H / 2 + 5;
    const maxR = Math.min(W, H) / 2 - 28;
    const n = radarDimensions.length;
    const angleStep = (Math.PI * 2) / n;

    // Grid rings
    for (let r = 1; r <= 4; r++) {
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const angle = -Math.PI / 2 + i * angleStep;
        const x = cx + Math.cos(angle) * (maxR * r / 4);
        const y = cy + Math.sin(angle) * (maxR * r / 4);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = "rgba(0,0,0,0.07)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Axis lines
    for (let i = 0; i < n; i++) {
      const angle = -Math.PI / 2 + i * angleStep;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
      ctx.strokeStyle = "rgba(0,0,0,0.08)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // History polygon
    ctx.beginPath();
    radarDimensions.forEach((d, i) => {
      const angle = -Math.PI / 2 + i * angleStep;
      const r = (d.history / 100) * maxR;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = "rgba(148,163,184,0.15)";
    ctx.fill();
    ctx.strokeStyle = "rgba(148,163,184,0.5)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Current polygon with animation
    const animScale = 0.95 + 0.05 * Math.sin(phase);
    ctx.beginPath();
    radarDimensions.forEach((d, i) => {
      const angle = -Math.PI / 2 + i * angleStep;
      const r = (d.current / 100) * maxR * animScale;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = "rgba(232,83,26,0.15)";
    ctx.fill();
    ctx.strokeStyle = "#E8531A";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Dots on current
    radarDimensions.forEach((d, i) => {
      const angle = -Math.PI / 2 + i * angleStep;
      const r = (d.current / 100) * maxR * animScale;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A";
      ctx.fill();
    });

    // Labels
    radarDimensions.forEach((d, i) => {
      const angle = -Math.PI / 2 + i * angleStep;
      const labelR = maxR + 16;
      const x = cx + Math.cos(angle) * labelR;
      const y = cy + Math.sin(angle) * labelR;
      ctx.fillStyle = "#6b7280";
      ctx.font = "bold 9px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(d.label, x, y);
    });

    radarAnimRef.current = requestAnimationFrame(drawRadar);
  }, []);

  useEffect(() => {
    if (subTab !== "radar") {
      cancelAnimationFrame(radarAnimRef.current);
      return;
    }
    drawRadar();
    return () => cancelAnimationFrame(radarAnimRef.current);
  }, [subTab, drawRadar]);

  const generateReport = () => {
    setIsGenerating(true);
    setReportText("");
    const template = reportTemplates[selectedTemplate];
    const custom = customInput.trim();
    const reportContent = `【AI用气综合分析报告】
生成时间：2026年4月10日 · ${template}${custom ? ` · ${custom}` : ""}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

一、本月用气概况
• 本月已用气：25.9 m³（截至4月10日）
• 预计全月用气：31.2 m³
• 预计本月费用：¥107.6
• 当前余额：¥128.50（充足）

二、与历史同期对比
• 较去年同期（4月）：减少 2.3 m³（-6.9%）✓
• 较近6月平均：减少 3.0 m³（-8.4%）✓
• 较上月（3月）：持平（±0.5 m³）→
• 综合评价：本月用气控制良好，处于近6月最低水平

三、AI多维度评分对比
• 用气量控制：78分（历史均值85分）↑ 改善中
• 费用控制：82分（历史均值75分）↑ 优于历史
• 用气效率：88分（历史均值80分）↑ 持续提升
• 安全评分：92分（历史均值90分）↑ 保持良好
• 缴费及时：95分（历史均值88分）↑ 表现优秀
• 节能表现：85分（历史均值72分）↑ 显著提升

四、趋势预测
• 5月预计用气：22-26 m³（气温升高，用气减少）
• 5月预计费用：¥76-90
• 建议充值时间：4月25日前
• 建议充值金额：¥100（可覆盖未来2个月）

五、AI节能建议
1. 您的晚高峰（18-19点）用气量最高，建议错峰烹饪
2. 热水器温度建议从60°C调至55°C，可节省约12%燃气
3. 定期检查灶具密封性，避免微量泄漏造成浪费
4. 建议签约银行代扣，享受每月¥5话费补贴

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
报告由 AI 智慧燃气系统自动生成`;

    let i = 0;
    const interval = setInterval(() => {
      if (i < reportContent.length) {
        setReportText(prev => prev + reportContent[i]);
        i++;
      } else {
        clearInterval(interval);
        setIsGenerating(false);
      }
    }, 15);
    return () => clearInterval(interval);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="text-sm font-semibold text-gray-800">AI历史对比分析</div>

      {/* Sub tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[
          { key: "curve", label: "对比曲线" },
          { key: "radar", label: "雷达图" },
          { key: "report", label: "AI报告" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key as typeof subTab)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${subTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Curve */}
      {subTab === "curve" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-700">近6月用气量 + AI预测</span>
              <div className="flex items-center gap-3 text-[10px] text-gray-400">
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#E8531A]"></div>实际</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#E8531A] opacity-40"></div>预测</div>
              </div>
            </div>
            <canvas ref={curveRef} width={320} height={130} className="w-full" />
          </div>

          {/* Comparison table */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs font-semibold text-gray-700 mb-3">历史同期对比</div>
            <div className="space-y-2">
              {[
                { period: "去年同期(4月)", usage: 28.2, cost: 97.3, diff: -2.3, diffPct: -8.2 },
                { period: "近6月平均", usage: 29.9, cost: 103.3, diff: -4.0, diffPct: -13.4 },
                { period: "上月(3月)", usage: 25.9, cost: 89.4, diff: 0, diffPct: 0 },
                { period: "本月预测(4月)", usage: 31.2, cost: 107.6, diff: 5.3, diffPct: 20.5 },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <span className="text-xs text-gray-600">{row.period}</span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-700">{row.usage} m³</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      row.diff < 0 ? "bg-green-50 text-green-600" :
                      row.diff > 0 ? "bg-amber-50 text-amber-600" : "bg-gray-50 text-gray-500"
                    }`}>
                      {row.diff === 0 ? "持平" : `${row.diff > 0 ? "+" : ""}${row.diff.toFixed(1)} m³`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Radar */}
      {subTab === "radar" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-700">多维度评分雷达图</span>
              <div className="flex items-center gap-3 text-[10px] text-gray-400">
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#E8531A]"></div>本月</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-gray-400"></div>历史均值</div>
              </div>
            </div>
            <canvas ref={radarRef} width={320} height={220} className="w-full" />
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs font-semibold text-gray-700 mb-3">各维度详情</div>
            <div className="space-y-2">
              {radarDimensions.map((d, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-16 flex-shrink-0">{d.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2 relative">
                    <div className="h-2 rounded-full bg-gray-300" style={{ width: `${d.history}%` }} />
                    <div className="absolute top-0 h-2 rounded-full bg-[#E8531A]" style={{ width: `${d.current}%` }} />
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-xs font-bold text-[#E8531A]">{d.current}</span>
                    <span className={`text-[10px] ${d.current >= d.history ? "text-green-500" : "text-amber-500"}`}>
                      {d.current >= d.history ? `+${d.current - d.history}` : `${d.current - d.history}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Report */}
      {subTab === "report" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs font-semibold text-gray-700 mb-3">选择报告模板</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {reportTemplates.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedTemplate(i)}
                  className={`py-2 px-3 rounded-xl text-xs border-2 transition-colors cursor-pointer text-left ${selectedTemplate === i ? "border-[#E8531A] bg-orange-50 text-[#E8531A]" : "border-gray-100 text-gray-600"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="mb-3">
              <label className="text-xs text-gray-400 mb-1.5 block">自定义分析要求（选填）</label>
              <input
                type="text"
                value={customInput}
                onChange={e => setCustomInput(e.target.value)}
                placeholder="例如：重点分析冬季用气高峰原因..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-[#E8531A]"
              />
            </div>
            <button
              onClick={generateReport}
              disabled={isGenerating}
              className="w-full bg-[#E8531A] text-white py-3 rounded-xl text-sm font-medium cursor-pointer disabled:opacity-60 whitespace-nowrap"
            >
              {isGenerating ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-robot-2-line animate-pulse"></i>AI正在生成报告...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-robot-2-line"></i>生成AI分析报告
                </span>
              )}
            </button>
          </div>

          {reportText && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E8531A] flex items-center justify-center">
                    <i className="ri-robot-2-line text-white text-[10px]"></i>
                  </div>
                  <span className="text-xs font-semibold text-gray-800">AI分析报告</span>
                </div>
                <button className="text-xs text-[#E8531A] cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line mr-1"></i>导出
                </button>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 max-h-64 overflow-y-auto">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{reportText}</pre>
                {isGenerating && <span className="inline-block w-1 h-3 bg-[#E8531A] animate-pulse ml-0.5"></span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
